import { Subject, takeUntil } from 'rxjs';
import { LanguagesService } from '../services/languages.service';

export class CopyToClipboardPlugin {
    private currentTooltip: HTMLElement | null = null;

    constructor(
        selectorOrElement: string | HTMLElement,
        private languageService?: LanguagesService,
    ) {
        const elements: HTMLElement[] =
            typeof selectorOrElement === 'string'
                ? (Array.from(document.querySelectorAll(selectorOrElement)) as HTMLElement[])
                : [selectorOrElement];

        elements.forEach((element) => this.setupCopy(element));
    }

    private destroy$ = new Subject<void>();

    setupLanguageDirection(copyIcon: HTMLElement, container: HTMLElement): void {
        this.languageService?.selectedLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            const isRTL = (document.body.getAttribute('dir') || 'ltr') === 'rtl';

            copyIcon.style.left = '';
            copyIcon.style.right = '';
            container.style.paddingLeft = '';
            container.style.paddingRight = '';

            if (isRTL) {
                copyIcon.style.left = '0';
                container.style.paddingLeft = '20px';
            } else {
                copyIcon.style.right = '0';
                container.style.paddingRight = '20px';
            }
        });
    }

    private setupCopy(element: HTMLElement): void {
        const copyIcon = this.createCopyIcon(element);
        if (!copyIcon) return;

        element.addEventListener('mouseenter', () => (copyIcon.style.display = 'inline-block'));
        element.addEventListener('mouseleave', () => (copyIcon.style.display = 'none'));

        copyIcon.addEventListener('mouseenter', () =>
            this.showTooltip(copyIcon, 'default-cm-copyToClipboard', 'Copy to Clipboard', true),
        );
        copyIcon.addEventListener('mouseleave', () => this.removeTooltip());

        copyIcon.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.removeTooltip();

            const text = this.getTextToCopy(element);
            if (text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.showTooltip(copyIcon, 'default-cm-copied', 'Copied!', false);
                });
            }
        });
        this.setupLanguageDirection(copyIcon, element);
    }

    private createCopyIcon(container: HTMLElement): HTMLSpanElement | null {
        if (container.querySelector('.copy-icon')) return null;

        const isRTL = (document.body.getAttribute('dir') || 'ltr') === 'rtl';

        container.style.position = 'relative';
        container.style.display = 'inline-block';

        if (isRTL) {
            container.style.paddingLeft = '20px';
        } else {
            container.style.paddingRight = '20px';
        }

        const icon = document.createElement('span');
        icon.classList.add('copy-icon');
        icon.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.0669 10.6V11.8C10.0669 12.4627 9.52964 13 8.86689 13H2.8669C2.20415 13 1.6669 12.4627 1.6669 11.8V4.6C1.6669 3.93726 2.20415 3.4 2.86689 3.4H4.06689" stroke="#425B80" stroke-width="1.5"/>
                <rect x="12.4668" y="10.6" width="8.4" height="9.6" rx="1.2" transform="rotate(180 12.4668 10.6)" stroke="#425B80" stroke-width="1.5"/>
            </svg>
        `;

        icon.style.cssText = `
            display: none;
            cursor: pointer;
            background: transparent;
            padding: 2px;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
        `;

        if (isRTL) {
            icon.style.left = '0';
        } else {
            icon.style.right = '0';
        }

        container.appendChild(icon);
        return icon;
    }

    private getTextToCopy(container: HTMLElement): string {
        const slot = container.querySelector('slot') as HTMLSlotElement;
        if (slot) {
            const assignedNodes = slot.assignedNodes({ flatten: true });
            return assignedNodes
                .map((node) =>
                    node.nodeType === Node.TEXT_NODE ? node.textContent : (node as HTMLElement).innerText || '',
                )
                .join('')
                .trim();
        }
        return container.innerText.trim();
    }

    private showTooltip(target: HTMLElement, key: string, defaultMessage: string, persistent: boolean): void {
        const message = this.languageService?.getTranslation(key) || defaultMessage;
        this.removeTooltip();

        const rect = target.getBoundingClientRect();
        const isRTL = (document.body.getAttribute('dir') || 'ltr') === 'rtl';

        const tooltip = document.createElement('span');
        tooltip.classList.add('copy-tooltip');
        tooltip.textContent = message;
        tooltip.style.cssText = `
            position: fixed;
            top: ${rect.top - 30}px;
            ${isRTL ? `right: ${window.innerWidth - rect.right}px;` : `left: ${rect.left}px;`}
            background-color: #333;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            z-index: 9999;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        `;

        document.body.appendChild(tooltip);
        this.currentTooltip = tooltip;

        if (!persistent) {
            setTimeout(() => this.removeTooltip(), 1500);
        }
    }

    private removeTooltip(): void {
        if (this.currentTooltip?.parentNode) {
            this.currentTooltip.remove();
            this.currentTooltip = null;
        }
    }

    destroy(): void {
        this.removeTooltip();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
